import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  preferenciasDeAvisoService,
  type AdaptadorDePreferencias,
  type NivelDeAviso,
  type PreferenciasDeAviso,
} from "@/services/preferenciasDeAvisoService";

export const CHAVE_DAS_PREFERENCIAS_DE_AVISO = ["preferencias-de-aviso"] as const;

/**
 * A API interna como adaptador (D3, passo 19). Mora AQUI e não no serviço de propósito: o teste da tela do interno mocka
 * `@/services/preferenciasDeAvisoService` com uma factory que só tem `preferenciasDeAvisoService` (acessar outro export lança).
 */
export const ADAPTADOR_INTERNO: AdaptadorDePreferencias = {
  chaveDoCache: CHAVE_DAS_PREFERENCIAS_DE_AVISO,
  listar: () => preferenciasDeAvisoService.listar(),
  definir: (chave, nivel) => preferenciasDeAvisoService.definir(chave, nivel),
  restaurarPadroes: () => preferenciasDeAvisoService.restaurarPadroes(),
};

export function usePreferenciasDeAviso(adaptador: AdaptadorDePreferencias = ADAPTADOR_INTERNO) {
  return useQuery({
    queryKey: adaptador.chaveDoCache,
    queryFn: () => adaptador.listar(),
    staleTime: 30_000,
  });
}

/**
 * Salva UMA linha. A linha cuida do otimista e do "Não salvou"; aqui só o cache e a fila.
 *
 * O `scope` por categoria faz as gravações da MESMA linha saírem uma de cada vez, na ordem
 * dos cliques (o RadioGroup dispara uma mudança por passo da seta do teclado): sem ele, dois
 * PUTs em voo chegam ao servidor em qualquer ordem e a tela não sabe qual valeu. O escopo leva a
 * chave de cache do adaptador: a linha do interno e a do portal nunca se enfileiram uma na outra.
 */
export function useDefinirNivelDeAviso(
  chave: string,
  adaptador: AdaptadorDePreferencias = ADAPTADOR_INTERNO
) {
  const qc = useQueryClient();
  return useMutation({
    scope: { id: `${adaptador.chaveDoCache.join("/")}:${chave}` },
    // ⚠️ "always": com o padrão ("online") a mutação PAUSA sem rede, o PUT nem sai e a linha
    // fica em "Salvando…" para sempre. Assim ele sai, falha com o erro de rede e a tela cai no
    // "Não salvou" com o "Tentar de novo" (revisão final do D1, M7).
    networkMode: "always",
    mutationFn: (nivel: NivelDeAviso) => adaptador.definir(chave, nivel),
    onSuccess: (item) => {
      qc.setQueryData<PreferenciasDeAviso>(adaptador.chaveDoCache, (atual) =>
        atual
          ? {
              ...atual,
              categorias: atual.categorias.map((c) => (c.chave === item.chave ? item : c)),
            }
          : atual
      );
    },
  });
}

export function useRestaurarPadroesDeAviso(adaptador: AdaptadorDePreferencias = ADAPTADOR_INTERNO) {
  const qc = useQueryClient();
  return useMutation({
    // Mesmo motivo do `useDefinirNivelDeAviso`: sem rede, falhar é melhor que ficar pausado.
    networkMode: "always",
    mutationFn: () => adaptador.restaurarPadroes(),
    onSuccess: () => qc.invalidateQueries({ queryKey: adaptador.chaveDoCache }),
  });
}
